import React, { useEffect, useState } from 'react';

import {
  Grid,
  TextField,
  Button,
  Typography,
  Box,
  Container,
  useTheme,
} from '@mui/material';

import { Edit } from '@mui/icons-material';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { artistState, loadingState, toastState } from '../state/RecoilState';
import validator from 'validator';
import MfsRichTextEditor from '../components/Common/RichText/RichTextEditor';
import { blobUrl, uploadType } from '../Constants';
import MustPickArtist from '../components/Common/MustPickArtist';
import RestUtilities from '../services/RestUtilities';
import ModalFileManager from '../components/Common/ModalFileManager';
import { Artist, Upload } from '../@types/api';

const BandBiogContent = () => {
  const theme = useTheme();
  const setToast = useSetRecoilState(toastState);
  const [artist, setArtist] = useRecoilState(artistState);
  const [artistSummary, setArtistSummary] = useState<string | undefined>(
    artist?.artistSummary
  );
  const [hover, setHover] = useState<boolean>(false);
  const setLoading = useSetRecoilState(loadingState);
  const [editor, setEditor] = useState<any>(undefined);
  const [show, setShow] = useState<boolean>(false);
  const [isTouched, setIsTouched] = useState(false);

  const onUpdate = async () => {
    if (!editor) return;
    setLoading(true);
    const artistUpdate = {
      ...artist,
      artistBiog: editor.getHTML(),
      artistSummary,
    };
    const url = `/api/artist/Update/${artist?.artistId}`;
    const response = await RestUtilities.put(url, artistUpdate);
    if (response.ok) {
      setArtist(response.content);
      setArtistSummary(response.content.artistSummary);
      setToast('Artist Updated');
    } else {
      setToast('Something went wrong');
    }
    setLoading(false);
    setIsTouched(false);
  };

  useEffect(() => {
    setArtistSummary(artist?.artistSummary);
  }, [artist]);

  const onSummaryChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const text = validator.escape(e.currentTarget.value);
    setArtistSummary(text);
    setIsTouched(true);
  };

  const handleUploadSelect = (upload: Upload) => {
    if (!artist) return;
    const artistUpdate: Artist = {
      ...artist,
      photo: upload,
      artistPhoto: upload.uploadId,
    };
    setArtist(artistUpdate);
    setShow(false);
    setIsTouched(true);
  };

  if (!artist) {
    return <MustPickArtist />;
  }
  return (
    <Container maxWidth='lg'>
      <Typography variant='h4' gutterBottom>
        {artist.artistName} Biog and Summary
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs>
          <Box
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            sx={{ position: 'relative', overflow: 'hidden' }}
          >
            {hover && (
              <Box sx={maskStyles}>
                <Edit
                  onClick={() => setShow(true)}
                  sx={{
                    color: 'white',
                    cursor: 'pointer',
                    display: hover ? 'block' : 'none',
                  }}
                />
              </Box>
            )}
            <Box
              style={{
                backgroundImage: `url(${blobUrl}/images/${artist.photo?.fileName})`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center top',
                backgroundSize: 'cover',
                width: '600px',
                height: '300px',
                zIndex: 99,
              }}
            />
          </Box>
        </Grid>
        <Grid item xs>
          <Button
            variant='contained'
            color='primary'
            onClick={onUpdate}
            size='small'
            disabled={!isTouched}
            sx={{ mb: 2 }}
          >
            Save Changes
          </Button>
          <TextField
            multiline
            id='artist-summary'
            label='Summary'
            value={artistSummary ?? ''}
            fullWidth
            margin='normal'
            onChange={onSummaryChange}
            InputProps={{
              rows: 5,
              multiline: true,
              inputComponent: 'textarea',
            }}
            variant='outlined'
            sx={{ my: 1 }}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant='subtitle1' color={theme.palette.text.secondary}>
            Artist Biog
          </Typography>
          <Typography variant='body2' gutterBottom>
            We dont expect any Headings here. This is the Biography section on
            the main MFS website.
          </Typography>
          <Box sx={{ my: 1, pb: 3 }}>
            <MfsRichTextEditor
              setEditor={(editor) => setEditor(editor)}
              content={artist.artistBiog}
              setTouched={() => setIsTouched(true)}
              isSatellite={false}
            />
          </Box>
        </Grid>
      </Grid>
      <ModalFileManager
        show={show}
        handleClose={() => setShow(false)}
        fileType={uploadType.image}
        handleUploadSelect={handleUploadSelect}
      />
    </Container>
  );
};

export default BandBiogContent;

const maskStyles = {
  position: 'absolute',
  height: '100%',
  width: '100%',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(0,0,0,0.3)',
};
